// Raw fetch wrappers for the cashier multi-tenant auth flow. We use fetch here
// (not the shared axios client) because the staged flow carries a *pending*
// token that must be sent explicitly — the client only injects the final
// session token.

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '') as string;

export interface AuthUser { id: number; username: string; name: string; name_ar: string | null; tenant_slug: string }
export interface AuthSubsidiary { id: number; code: string; name: string }
export interface AuthBranch { id: number; code: string; name: string; subsidiary_id: number; has_cash_drawer: boolean; drawer_inactive?: boolean }
export interface AuthDrawer { id: number; code: string; name: string; branch_id: number }
export interface SessionInfo {
  user_id: number; username: string; tenant_db: string; tenant_slug: string;
  subsidiary_id: number; subsidiary_name: string; branch_id: number; branch_name: string;
  cash_drawer_id: number; cash_drawer_name: string;
}

export interface LoginResult { pending_token: string; user: AuthUser; available_subsidiaries: AuthSubsidiary[] }
export interface SubsidiaryResult { pending_token: string; subsidiary: AuthSubsidiary; available_branches: AuthBranch[] }
export interface BranchResult { pending_token: string; branch: AuthBranch; available_cash_drawers: AuthDrawer[] }
export interface DrawerResult { token: string; expires_at: string; session: SessionInfo }

export class AuthApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    /** The whole error body — SETUP_REQUIRED carries the wizard's token in it. */
    public readonly body: Record<string, unknown> | null = null,
  ) {
    super(code);
    this.name = 'AuthApiError';
  }
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const res = await fetch(`${BASE}/api/v1/cashier/auth${path}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new AuthApiError(json.error ?? 'REQUEST_FAILED', res.status, json as Record<string, unknown>);
  return json as T;
}

async function get<T>(path: string, token: string): Promise<T> {
  const res = await fetch(`${BASE}/api/v1/cashier/auth${path}`, {
    headers: { accept: 'application/json', authorization: `Bearer ${token}` },
  });
  const json = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new AuthApiError(json.error ?? 'REQUEST_FAILED', res.status);
  return json as T;
}

export const authApi = {
  login: (tenant_slug: string, username: string, password: string) =>
    post<LoginResult>('/login', { tenant_slug, username, password }),
  /** Hydrate session info from an externally-supplied session JWT (SSO handoff). */
  me: (token: string) => get<{ session: SessionInfo }>('/me', token),
  selectSubsidiary: (subsidiary_id: number, token: string) =>
    post<SubsidiaryResult>('/select-subsidiary', { subsidiary_id }, token),
  selectBranch: (branch_id: number, token: string) =>
    post<BranchResult>('/select-branch', { branch_id }, token),
  selectDrawer: (cash_drawer_id: number, token: string) =>
    post<DrawerResult>('/select-drawer', { cash_drawer_id }, token),
};
